import React from "react";
import { T } from "../../../app/tokens.js";
import { contractualAreaYear } from "../../../../shared/leaseAreaRent.js";
import { convertAmount } from "../../../utils/fxConvert.js";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const money = (value, currency, displayCurrency, rates, decimals = 0) => {
  const amount = convertAmount(value, currency, displayCurrency, rates);
  return amount == null ? "Unavailable" : new Intl.NumberFormat("en-US", {
    style: "currency", currency: displayCurrency, maximumFractionDigits: decimals, minimumFractionDigits: decimals,
  }).format(amount);
};

export function AreaContractualRentDetail({ areas, row, currency, displayCurrency, rates }) {
  const detail = contractualAreaYear(areas, row.year, row.contractual, row.currency || currency, currency);
  const rec = detail.reconciliation;
  return <div data-testid={`area-contractual-detail-${row.year}`} style={{ fontSize: T.fsXs, color: T.mid }}>
    <div>Sourced contractual rent · native {currency} · no monthly net/abatement allocation</div>
    <div style={{ overflowX: "auto", marginTop: 8 }}>
      <div data-testid={`rent-schedule-month-strip-${row.year}`}
        style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(72px, 1fr))", gap: 5 }}>
        {detail.months.map((month) => <div key={month.month}
          data-testid={`rent-schedule-month-${row.year}-${month.month}`}
          data-month-source={month.complete ? "area_rates" : "unresolved"}
          title={month.issues.join("; ")}
          style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: "6px 3px", textAlign: "center", background: T.card }}>
          <div>{MONTHS[month.month - 1]}</div>
          <strong style={{ display: "block", color: T.dark, marginTop: 3 }}>
            {money(month.contractual, currency, displayCurrency, rates)}
          </strong>
          <div title="Annualized rate using the same month's contributing gross footage">
            {month.annualizedPsf == null ? "Rate unavailable" : `${money(month.annualizedPsf, currency, displayCurrency, rates, 2)}/sf/yr`}
          </div>
        </div>)}
      </div>
    </div>
    <div data-testid={`area-rent-reconciliation-${row.year}`} role={rec.status === "mismatch" ? "alert" : undefined}
      style={{ marginTop: 9, color: rec.status === "matched" ? T.forestGreen : T.mid }}>
      {rec.status === "incomplete" ? `Incomplete coverage: ${rec.completeMonths}/12 complete months. Annual detail reconciliation unavailable.`
        : rec.status === "matched" ? `Reconciled within ${rec.tolerance.toFixed(2)} ${currency}.`
          : rec.status === "mismatch" ? "Warning: contractual detail does not match the recorded annual anchor."
            : rec.status === "currency_mismatch" ? "Annual anchor currency differs; native reconciliation unavailable."
              : "Recorded annual rent missing; reconciliation unavailable."}
      <div>Anchor: {rec.anchor == null ? "Unknown" : `${rec.anchor.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${rec.anchorCurrency}`}
        {" · "}Complete detail: {rec.detailSum == null ? "Unavailable" : `${rec.detailSum.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`}
        {" · "}Delta (detail − anchor): {rec.delta == null ? "Unavailable" : `${rec.delta.toFixed(2)} ${currency}`}</div>
    </div>
    {detail.months.some((month) => month.issues.length) && <details style={{ marginTop: 6 }}>
      <summary>Unresolved month coverage</summary>
      {detail.months.filter((month) => month.issues.length).map((month) =>
        <div key={month.month}>{MONTHS[month.month - 1]}: {month.issues.join("; ")}</div>)}
    </details>}
  </div>;
}
